import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Plus,
  Save,
  Wand2,
  Loader2,
  AlertTriangle,
  Maximize2,
  Minimize2,
  Lock,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { Container3DViewer } from './container-3d-viewer';
import { ContainerEditor3D } from './container-editor-3d';
import { ContainerItemsTable } from './container-items-table';
import { ContainerItemFormDialog } from './container-item-form-dialog';
import { ContainerFormDialog } from './container-form-dialog';
import { StowFromOrderPanel } from './stow-from-order-panel';
import { FillIndicator } from './fill-indicator';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { packContainer, type Placement, type PackingItem } from '../utils/bin-packing';
import { agruparUnidades } from '../utils/unit-positions';
import type {
  ContainerType,
  Container,
  ContainerItem,
  BulkPositionUpdate,
} from '../types/import-tracking.types';

interface ContainerDetailPanelProps {
  container: Container;
  containerType: ContainerType;
  items: ContainerItem[];
  onRefresh: () => void;
  /**
   * Por qué no se puede editar el armado, o `null` si sí se puede.
   *
   * Lo calcula el padre a partir del estado de la orden — el panel solo conoce
   * el contenedor. Si viene con texto, el editor 3D pasa a solo lectura: el
   * armado de una carga ya entregada no se reescribe.
   */
  motivoSoloLectura?: string | null;
}

export function ContainerDetailPanel({
  container,
  containerType,
  items,
  onRefresh,
  motivoSoloLectura,
}: ContainerDetailPanelProps) {
  const { toast } = useToast();
  const { deleteContainerItem, saveContainerPositions, isSavingPositions } =
    useImportTrackingStore();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [placements, setPlacements] = useState<Placement[] | undefined>(undefined);
  const [packingResult, setPackingResult] = useState<{
    usedVolumeCbm: number;
    usedWeightKg: number;
    fillPercent: number;
  } | null>(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editItem, setEditItem] = useState<ContainerItem | undefined>(undefined);
  const [isExpanded, setIsExpanded] = useState(false);
  /** El alta del contenedor siguiente, sin salir de esta pantalla. */
  const [crearAbierto, setCrearAbierto] = useState(false);
  /** `view` solo mira; `edit` habilita el acomodo manual arrastrando la carga. */
  /**
   * Un solo modo: el armado 3D.
   *
   * 🔴 Había dos. "Ver" traía su propio Auto-Empacar con su propio guardado, y
   * "Editar" traía el editor arrastrable con OTRO auto-acomodo y OTRO guardado.
   * Dos motores para lo mismo, y solo uno guardaba bien — el usuario acomodaba
   * en uno y perdía el trabajo.
   *
   * Ahora el editor es la pantalla. Auto-acomodar vive adentro, y apenas
   * termina las cajas ya se pueden arrastrar: no hay que cambiar de modo para
   * corregir a mano lo que el automático dejó torcido.
   *
   * Queda como estado —y no como constante— porque la vista de solo lectura
   * sigue existiendo para una orden ya entregada, donde no hay nada que
   * acomodar. Lo que se sacó es el interruptor.
   */
  const [mode] = useState<'view' | 'edit'>('edit');

  const handleAutoPack = useCallback(() => {
    if (items.length === 0) {
      toast({
        title: 'Sin items',
        description: 'Agregue items al contenedor antes de empacar.',
        variant: 'warning',
      });
      return;
    }

    const packingItems: PackingItem[] = items.map((item) => ({
      id: item.id,
      lengthCm: item.lengthCm,
      widthCm: item.widthCm,
      heightCm: item.heightCm,
      weightKg: item.weightKg,
      quantity: item.quantity,
      color: item.color,
    }));

    const result = packContainer(
      {
        lengthCm: containerType.lengthCm,
        widthCm: containerType.widthCm,
        heightCm: containerType.heightCm,
        maxWeightKg: containerType.maxWeightKg,
        maxVolumeCbm: containerType.maxVolumeCbm,
      },
      packingItems
    );

    setPlacements(result.placements);
    setPackingResult({
      usedVolumeCbm: result.usedVolumeCbm,
      usedWeightKg: result.usedWeightKg,
      fillPercent: result.fillPercent,
    });

    /*
      El resumen de lo que no entró se mudó a la bandeja del editor
      (`container-editor-3d.tsx`), que es quien realmente deja carga afuera. Acá
      solo queda el aviso de que salió bien.
    */
    if (result.unplacedItems.length === 0) {
      toast({
        title: 'Empaquetado completado',
        description: `${result.placements.length} item(s) colocados. Llenado: ${result.fillPercent.toFixed(1)}%`,
        variant: 'success',
      });
    }
  }, [items, containerType, toast]);

  const handleSavePositions = useCallback(async () => {
    if (!placements || placements.length === 0) {
      toast({
        title: 'Sin posiciones',
        description: 'Ejecute "Auto-Empacar" primero para generar posiciones.',
        variant: 'warning',
      });
      return;
    }

    try {
      /*
        🔴 Acá se perdía el auto-empaque.

        Cada bulto se mandaba como una entrada propia, con el id de la LÍNEA
        recortado: 470 entradas apuntando a 6 filas. El backend recorre y
        actualiza, así que cada fila terminaba con la posición del ÚLTIMO bulto
        y las otras 469 se perdían. Salía "posiciones guardadas" y al recargar
        había una caja por producto.

        `agruparUnidades` manda una entrada por línea con el acomodo completo.
      */
      await saveContainerPositions(container.id, {
        positions: agruparUnidades(
          placements.map((p) => ({
            id: p.itemId,
            x: p.x,
            y: p.y,
            z: p.z,
            rotated: p.rotated,
          }))
        ),
        usedVolumeCbm: packingResult?.usedVolumeCbm ?? 0,
        usedWeightKg: packingResult?.usedWeightKg ?? 0,
        fillPercent: packingResult?.fillPercent ?? 0,
      });

      toast({
        title: 'Posiciones guardadas',
        description: 'Las posiciones de los items fueron guardadas exitosamente.',
        variant: 'success',
      });
      onRefresh();
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudieron guardar las posiciones.',
        variant: 'destructive',
      });
    }
  }, [placements, packingResult, container.id, saveContainerPositions, onRefresh, toast]);

  /**
   * Guardado del acomodo manual. El editor ya arma el payload completo.
   *
   * Deja que el error se propague: el editor lo muestra al lado del botón, que
   * es donde el usuario está mirando. Solo se le da un mensaje entendible.
   */
  const handleSaveLayout = useCallback(
    async (payload: BulkPositionUpdate) => {
      try {
        await saveContainerPositions(container.id, payload);
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'No se pudo guardar el acomodo';
        throw new Error(message);
      }

      toast({
        title: 'Acomodo guardado',
        description: `${payload.positions.length} item(s) ubicados · ${payload.fillPercent.toFixed(1)}% de llenado`,
        variant: 'success',
      });

      // El auto-empacado anterior deja de valer: ahora manda lo guardado.
      setPlacements(undefined);
      setPackingResult(null);
      onRefresh();
    },
    [container.id, saveContainerPositions, toast, onRefresh]
  );

  const handleDeleteItem = async (itemId: string) => {
    await deleteContainerItem(container.id, itemId);
    // Limpiar placements stale si habian
    setPlacements(undefined);
    setPackingResult(null);
    onRefresh();
  };

  const handleEditItem = (item: ContainerItem) => {
    setEditItem(item);
    setShowItemForm(true);
  };

  const handleAddItem = () => {
    setEditItem(undefined);
    setShowItemForm(true);
  };

  const handleItemFormSuccess = () => {
    // Limpiar placements stale cuando se agrega/edita un item
    setPlacements(undefined);
    setPackingResult(null);
    onRefresh();
  };

  // Use packing result if available, otherwise use container data
  const displayVolume = packingResult?.usedVolumeCbm ?? container.usedVolumeCbm;
  const displayWeight = packingResult?.usedWeightKg ?? container.usedWeightKg;
  const displayFill = packingResult?.fillPercent ?? container.fillPercent;

  // Visor 3D expandido (fullscreen overlay)
  if (isExpanded) {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Toolbar expandido */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-background shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={handleAddItem}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar Item
              </Button>
              {mode === 'view' && (
                <>
                  <Button size="sm" variant="outline" onClick={handleAutoPack}>
                    <Wand2 className="w-4 h-4 mr-1" />
                    Auto-Empacar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSavePositions}
                    disabled={isSavingPositions || !placements}
                  >
                    {isSavingPositions ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Guardar Posiciones
                  </Button>
                  {placements && packingResult && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1 ml-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Posiciones sin guardar
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <FillIndicator
                usedVolumeCbm={displayVolume}
                maxVolumeCbm={containerType.maxVolumeCbm}
                usedWeightKg={displayWeight}
                maxWeightKg={containerType.maxWeightKg}
                fillPercent={displayFill}
                compact
              />
              <Button size="sm" variant="outline" onClick={() => setIsExpanded(false)}>
                <Minimize2 className="w-4 h-4 mr-1" />
                Minimizar
              </Button>
            </div>
          </div>

          {/* Contenido expandido */}
          {mode === 'edit' ? (
            // El editor ya trae su propio panel de métricas, bandeja y secuencia.
            <div className="flex-1 overflow-y-auto p-4">
              {motivoSoloLectura && <AvisoSoloLectura motivo={motivoSoloLectura} />}
              <ContainerEditor3D
                containerType={containerType}
                items={items}
                status={container.status}
                onSave={handleSaveLayout}
                readOnly={!!motivoSoloLectura}
                onCrearContenedor={() => setCrearAbierto(true)}
                className="h-full"
              />
            </div>
          ) : (
            <div className="flex flex-1 overflow-hidden">
              {/* 3D Viewer - ocupa todo el espacio */}
              <div className="flex-1">
                <Container3DViewer
                  containerType={containerType}
                  items={items}
                  placements={placements}
                  selectedItemId={selectedItemId}
                  onItemClick={setSelectedItemId}
                  status={container.status}
                  fillPercent={displayFill}
                />
              </div>

              {/* Panel lateral con tabla de items */}
              <div className="w-80 border-l overflow-y-auto bg-background p-3 shrink-0">
                <h3 className="text-sm font-semibold mb-2">Items ({items.length})</h3>
                <ContainerItemsTable
                  items={items}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  isLoading={false}
                  selectedItemId={selectedItemId}
                  onSelect={setSelectedItemId}
                />
              </div>
            </div>
          )}
        </div>

        {/* Item Form Dialog (debe estar fuera del layout para que se vea sobre el fullscreen) */}
        <ContainerItemFormDialog
          containerId={container.id}
          open={showItemForm}
          onOpenChange={setShowItemForm}
          item={editItem}
          onSuccess={handleItemFormSuccess}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/*
        El aviso va PRIMERO de todo.

        🔴 En el primer intento quedó pegado arriba del editor 3D, pero antes del
        editor está el panel de estibar — así que lo primero que veía alguien en
        una orden entregada eran botones para cargar bultos, y el aviso aparecía
        recién después de bajar. Un aviso que llega tarde no avisa.
      */}
      {motivoSoloLectura && <AvisoSoloLectura motivo={motivoSoloLectura} />}

      {/*
        Traer los productos de la orden es el camino principal: ya están cargados
        con sus medidas desde la proforma. "Agregar Item" queda como excepción —
        un pallet de muestras, algo que no está en la orden.

        Con el armado cerrado no se ofrece: estibar es la acción de escritura más
        grande de esta pantalla y el backend la rechaza.
      */}
      {!motivoSoloLectura && <StowFromOrderPanel containerId={container.id} onStowed={onRefresh} />}

      {/*
        🔴 Acá había un bloque de "N unidad(es) no entran en este contenedor" con
        la cuenta de m³, kg y contenedores necesarios, y el botón para crear el
        siguiente. **Nunca se mostraba.**

        Dependía de `unplaced`, que solo se setea en `handleAutoPack` — la función
        del botón "Auto-Empacar" del modo `view`. Y `mode` quedó fijo en `'edit'`
        cuando se unificaron los dos armados, así que ese botón dejó de dibujarse
        y con él murió el aviso. Era una función completa, escrita y sin uso: el
        usuario pedía "el modal del segundo contenedor" y nadie encontraba de
        dónde salía, porque no salía de ningún lado.

        Ahora vive donde está el dato real: la **bandeja del editor** —
        `container-editor-3d.tsx` — que sí sabe qué no entró porque es quien lo
        deja afuera. Ahí está el aviso, la cuenta y el botón, todo junto.

        Lección: cuando se unifican dos caminos, hay que buscar qué colgaba del
        que se apagó.
      */}

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Los tres botones escriben. Con el armado cerrado no se ofrecen. */}
          {!motivoSoloLectura && (
            <Button size="sm" variant="outline" onClick={handleAddItem}>
              <Plus className="w-4 h-4 mr-1" />
              Agregar suelto
            </Button>
          )}
          {mode === 'view' && !motivoSoloLectura && (
            <>
              <Button size="sm" variant="outline" onClick={handleAutoPack}>
                <Wand2 className="w-4 h-4 mr-1" />
                Auto-Empacar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSavePositions}
                disabled={isSavingPositions || !placements}
              >
                {isSavingPositions ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Guardar Posiciones
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mode === 'view' && placements && packingResult && (
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              Posiciones sin guardar
            </div>
          )}
        </div>
      </div>

      {mode === 'edit' ? (
        <div className="space-y-4">
          <ContainerEditor3D
            containerType={containerType}
            items={items}
            status={container.status}
            onSave={handleSaveLayout}
            readOnly={!!motivoSoloLectura}
            onCrearContenedor={() => setCrearAbierto(true)}
          />
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Items ({items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ContainerItemsTable
                items={items}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
                isLoading={false}
                selectedItemId={selectedItemId}
                onSelect={setSelectedItemId}
              />
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Main content */
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* 3D Viewer - 3/5 */}
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardContent className="p-2 relative h-[500px]">
                <Button
                  size="icon"
                  variant="outline"
                  className="absolute top-4 right-4 z-10 h-8 w-8 bg-background/80 backdrop-blur-sm"
                  onClick={() => setIsExpanded(true)}
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
                <Container3DViewer
                  containerType={containerType}
                  items={items}
                  placements={placements}
                  selectedItemId={selectedItemId}
                  onItemClick={setSelectedItemId}
                  status={container.status}
                  fillPercent={displayFill}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <FillIndicator
                  usedVolumeCbm={displayVolume}
                  maxVolumeCbm={containerType.maxVolumeCbm}
                  usedWeightKg={displayWeight}
                  maxWeightKg={containerType.maxWeightKg}
                  fillPercent={displayFill}
                />
              </CardContent>
            </Card>
          </div>

          {/* Items Table - 2/5 */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Items ({items.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ContainerItemsTable
                  items={items}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  isLoading={false}
                  selectedItemId={selectedItemId}
                  onSelect={setSelectedItemId}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Item Form Dialog */}
      <ContainerItemFormDialog
        containerId={container.id}
        open={showItemForm}
        onOpenChange={setShowItemForm}
        item={editItem}
        onSuccess={handleItemFormSuccess}
      />

      {/*
        El contenedor siguiente, precargado del actual.

        Son hermanos del mismo embarque: mismo tipo de equipo, mismos puertos y
        mismas fechas. Volver a pedir esos cinco datos sería pedirle a la persona
        que copie a mano lo que está mirando.
      */}
      <ContainerFormDialog
        orderId={container.orderId}
        open={crearAbierto}
        onOpenChange={setCrearAbierto}
        defaults={{
          containerTypeId: container.containerTypeId,
          originPort: container.originPort ?? null,
          destinationPort: container.destinationPort ?? null,
          etd: container.etd ?? null,
          eta: container.eta ?? null,
          sourceLabel: 'Del contenedor que estás viendo',
        }}
        onSuccess={() => {
          onRefresh();
          toast({
            title: 'Contenedor creado',
            description:
              'Está en la pestaña Contenedores de la orden. Estibá ahí lo que no entró acá.',
            variant: 'success',
          });
        }}
      />
    </div>
  );
}

/**
 * El aviso de que el armado quedó cerrado.
 *
 * Va ARRIBA del editor y no adentro: quien abre esta pantalla espera poder
 * arrastrar bultos, y descubrir que no se puede recién al intentarlo —sin que
 * nada explique por qué— se lee como que la aplicación se rompió.
 */
function AvisoSoloLectura({ motivo }: { motivo: string }) {
  return (
    <p className="mb-3 flex items-start gap-2 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2 text-xs text-foreground">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alerta" />
      <span>
        <strong>Solo lectura.</strong> {motivo}
      </span>
    </p>
  );
}

// ── Piezas auxiliares ────────────────────────────────────────────────────────

/*
  Acá vivía `ModeSwitch`, el interruptor entre "Vista" y "Armado 3D".

  Se sacó porque eran dos motores para lo mismo: cada modo traía su propio
  auto-acomodo y su propio guardado, y solo uno guardaba bien. Quien acomodaba
  en uno perdía el trabajo al pasar al otro. Ahora el armado 3D es la pantalla,
  y auto-acomodar vive adentro.
*/

/*
  Acá vivía `MultiQuantityNotice`, un aviso que decía:

    "El armado 3D los ubica como un bulto único, porque cada item guarda una
     sola posición. Para acomodarlos por separado hay que cargarlos como
     registros individuales."

  Era cierto y dejó de serlo: `unitPositions` guarda dónde va cada unidad y el
  editor las dibuja y las mueve una por una. Un aviso que describe una
  limitación que ya no existe es peor que ninguno — le dice a la gente que no
  intente algo que sí funciona.
*/
